import React, { useMemo, useState, useEffect, useRef, useLayoutEffect } from "react";
import { motion, useMotionValue, useSpring, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { FaArrowRight, FaGlobeAsia, FaHeart, FaLeaf, FaQuoteLeft, FaCrown, FaFacebookF, FaInstagram, FaYoutube, FaOm } from "react-icons/fa";
import { FiAward, FiMapPin, FiCalendar, FiGlobe, FiStar, FiX, FiPlay, FiUsers, FiTrendingUp, FiShield, FiZap, FiArrowRight, FiHeart, FiCompass, FiUsers as FiUsersAlt } from "react-icons/fi";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import CountUp from "react-countup";

gsap.registerPlugin(ScrollTrigger);

const testimonials = [
  { name: "Priya S.",    location: "Mumbai",        review: "Divyyog Foundation completely changed my relationship with my body and mind. Mrs. Divya Nithin Bhojwani's teaching is deeply personal and spiritually enriching." },
  { name: "Ravi K.",     location: "United States",  review: "The online classes are so well structured. I feel like I'm in a real classroom even from thousands of miles away." },
  { name: "Ananya R.",   location: "Pune",           review: "From weight management to stress relief my transformation started the day I joined Divyyog's online program." },
  { name: "Mohammed A.", location: "Dubai",          review: "The flexibility of scheduling and the quality of instruction is unmatched. I attend classes from Dubai and never miss a session!" },
];

const stats = [
  { number: "1000+", label: "Students Enrolled" },
  { number: "15+",   label: "Countries Reached" },
  { number: "50+",   label: "Live Sessions Monthly" },
  { number: "20+",   label: "Courses Available" },
  { number: "98%",   label: "Satisfaction Rate" },
];

const specializations = [
  "Classical Hatha Yoga", "Pranayama & Meditation", "Therapeutic Yoga",
  "Yoga for Weight Management", "Prenatal & Postnatal Yoga", "Kids Yoga",
];
const gpu = { willChange:"transform,opacity", transform:"translateZ(0)", backfaceVisibility:"hidden" };

const vp = (s,e) => ({ hidden:{ opacity:0, y:s }, show:{ opacity:1, y:0, transition:{ duration:0.85, ease:[0.22,1,0.36,1] } } });
const stagger = { hidden:{}, show:{ transition:{ staggerChildren:0.11 } } };
const fadeUp = vp(50,0);

// Magnetic hover card
function MagCard({ children, className="", style={} }) {
  const ref = useRef(null);
  const x = useMotionValue(0); const y = useMotionValue(0);
  const sx = useSpring(x,{stiffness:200,damping:20});
  const sy = useSpring(y,{stiffness:200,damping:20});
  const handleMove = e => {
    if(!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set(((e.clientX-r.left)/r.width-0.5)*18);
    y.set(((e.clientY-r.top)/r.height-0.5)*18);
  };
  return (
    <motion.div ref={ref} onMouseMove={handleMove} onMouseLeave={()=>{x.set(0);y.set(0);}}
      style={{ rotateX:sy, rotateY:sx, transformStyle:"preserve-3d", transformPerspective:800, ...style }}
      className={className}>
      {children}
    </motion.div>
  );
}


// Gallery images   high quality yoga/championship
const GALLERY = [
  { src:"/assets/achievement4.jpeg",  label:"Gold Medal Moment",        tag:"🥇 Gold" },
  { src:"/assets/achievement2.jpeg",  label:"Championship Performance",  tag:"Masters" },
  { src:"/assets/achievement3.jpeg",           tag:"World Stage" },
  { src:"/assets/achievement1.jpeg",  label:"Award Ceremony",            tag:"Ahmedabad" },
  { src:"/assets/achievement5.jpeg",  label:"India's Pride",             tag:"2026" },
  { src:"/assets/gallery/image64.jpg",  label:"Podium Moment",             tag:"1st Place" },
];

const COUNTRIES = [
  "🇳🇿 New Zealand","🇷🇴 Romania","🇳🇱 Netherlands","🇴🇲 Oman","🇺🇿 Uzbekistan",
  "🇺🇸 United States","🇧🇼 Botswana","🇮🇹 Italy","🇸🇨 Seychelles","🇦🇺 Australia",
  "🇨🇦 Canada","🇯🇵 Japan","🇩🇪 Germany","🇧🇷 Brazil","🇫🇷 France",
  "🇿🇦 South Africa","🇦🇪 UAE","🇸🇬 Singapore","🇲🇾 Malaysia","🇬🇧 United Kingdom",
];

const DIGS = ["Dr. Jaydev Arya","Dr. Sanjay Malpani","Dr. Anand Balyogi","Dr. Aarti Pal","Dr. C. V. Jayanti","Rohit Kaushik"];

// Core pillars for the "What Is Divyyog Foundation" section
const FOUNDATION_PILLARS = [
  { icon: FaOm, title: "Authentic Roots", text: "Classical yogic tradition passed down through disciplined sadhana, Vedic study, and revered lineage." },
  { icon: FiHeart, title: "Holistic Wellness", text: "Mind-body-spirit programs crafted for lasting physical vitality, mental clarity, and emotional equilibrium." },
  { icon: FiCompass, title: "Global Guidance", text: "Certified master instruction accessible to seekers anywhere in the world through interactive live and in-person sessions." },
  { icon: FiUsersAlt, title: "Inclusive Community", text: "A growing global family of dedicated practitioners spanning all age groups, backgrounds, and nations." },
];

const SOCIAL_LINKS = [
  {
    Icon: FaFacebookF,
    label: "Facebook",
    href: "https://www.facebook.com/people/Divyyogfoundation-Gondia/61558710747446/",
    background: "linear-gradient(135deg, #1877F2, #0d65d9)",
    shadow: "rgba(24, 119, 242, 0.35)",
  },
  {
    Icon: FaInstagram,
    label: "Instagram",
    href: "https://www.instagram.com/divya_bhojwani11/",
    background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
    shadow: "rgba(220, 39, 67, 0.4)",
  },
  {
    Icon: FaYoutube,
    label: "YouTube",
    href: "https://youtube.com",
    background: "linear-gradient(135deg, #FF0000, #cc0000)",
    shadow: "rgba(255, 0, 0, 0.35)",
  },
];

// GSAP-powered magnetic button   pulls toward the cursor, snaps back with an elastic ease
function MagneticButton({ children, className = "", as: As = "button", ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const strength = 22;
    const handleMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left - r.width / 2) / r.width;
      const y = (e.clientY - r.top - r.height / 2) / r.height;
      gsap.to(el, { x: x * strength, y: y * strength, duration: 0.5, ease: "power3.out" });
    };
    const handleLeave = () => gsap.to(el, { x: 0, y: 0, duration: 0.6, ease: "elastic.out(1,0.4)" });
    el.addEventListener("mousemove", handleMove);
    el.addEventListener("mouseleave", handleLeave);
    return () => {
      el.removeEventListener("mousemove", handleMove);
      el.removeEventListener("mouseleave", handleLeave);
    };
  }, []);
  return (
    <As ref={ref} className={className} {...props}>
      {children}
    </As>
  );
}

// GSAP cursor-based 3D tilt wrapper   used for the Achievement History card
function TiltCard({ children, className = "", strength = 9 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const qx = gsap.quickTo(el, "rotateY", { duration: 0.6, ease: "power3.out" });
    const qy = gsap.quickTo(el, "rotateX", { duration: 0.6, ease: "power3.out" });
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      qx((((e.clientX - r.left) / r.width) - 0.5) * strength);
      qy((((e.clientY - r.top) / r.height) - 0.5) * -strength);
    };
    const onLeave = () => { qx(0); qy(0); };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [strength]);
  return (
    <div ref={ref} className={className} style={{ transformStyle: "preserve-3d", transformPerspective: 1400, willChange: "transform" }}>
      {children}
    </div>
  );
}

// GSAP-powered flip/tilt pillar card for the "What Is DivyYoga Foundation" section
function PillarCard({ icon: Icon, title, text, index }) {
  const cardRef = useRef(null);
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      gsap.to(el, { rotateY: px * 18, rotateX: py * -18, duration: 0.6, ease: "power3.out", transformPerspective: 900 });
    };
    const onLeave = () => gsap.to(el, { rotateY: 0, rotateX: 0, duration: 0.8, ease: "elastic.out(1,0.5)" });
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className="pillar-card group relative overflow-hidden rounded-[32px] border border-[#F4D9B5] bg-white/70 backdrop-blur-2xl p-8 shadow-[0_20px_70px_rgba(0,0,0,0.05)]"
      style={{ transformStyle: "preserve-3d", willChange: "transform" }}
    >
      <div className="absolute -inset-[2px] rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: "conic-gradient(from 0deg, #F29A0C, #FFD166, transparent 40%, #F29A0C)", filter: "blur(8px)", zIndex: -1 }} />
      <div className="absolute top-0 left-0 right-0 h-[3px] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500"
        style={{ background: "linear-gradient(90deg,transparent,#F29A0C,#FFD166,transparent)" }} />
      <div style={{ transform: "translateZ(30px)" }}>
        <div className="w-16 h-16 rounded-[22px] bg-gradient-to-br from-[#F29A0C] to-[#FF7A00] flex items-center justify-center text-white text-2xl shadow-[0_15px_40px_rgba(242,154,12,0.28)] transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6">
          <Icon />
        </div>
        <h3 className="text-[#111111] text-[22px] font-semibold mt-6" style={{ fontFamily: "Poppins" }}>{title}</h3>
        <p className="text-[#666666] text-[15px] leading-[28px] mt-3">{text}</p>
        <span className="absolute bottom-4 right-6 text-[64px] font-semibold opacity-[0.05] leading-none select-none">
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════
// Cinematic video showcase   contained width, true 16:9, GSAP cursor-tilt frame
// ══════════════════════════════════════════
function VideoShowcase({ embedUrl, title, subtitle }) {
  const wrapRef = useRef(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const qx = gsap.quickTo(el, "rotateY", { duration: 0.7, ease: "power3.out" });
    const qy = gsap.quickTo(el, "rotateX", { duration: 0.7, ease: "power3.out" });
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      qx((((e.clientX - r.left) / r.width) - 0.5) * 6);
      qy((((e.clientY - r.top) / r.height) - 0.5) * -6);
    };
    const onLeave = () => { qx(0); qy(0); };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.97 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="max-w-[980px] mx-auto"
    >
      <div ref={wrapRef} className="group relative" style={{ transformPerspective: 1400, transformStyle: "preserve-3d", willChange: "transform" }}>
        {/* soft glow behind the frame */}
        <div className="absolute -inset-6 rounded-[50px] opacity-70 blur-2xl pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(242,154,12,0.22), transparent 70%)" }} />

        {/* slim animated gradient border */}
        <div className="relative rounded-[36px] p-[2px] overflow-hidden shadow-[0_40px_120px_rgba(242,154,12,0.18)]"
          style={{ background: "linear-gradient(120deg,#F29A0C,#FFD166,#6B8A5A,#F29A0C)", backgroundSize: "300% 300%", transform: "translateZ(30px)" }}>
          <div className="relative rounded-[34px] overflow-hidden bg-[#0A0500]">
            {/* video, true 16:9 */}
            <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src={embedUrl}
                title={title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>

            {/* floating live badge, top-right, overlapping video */}
            <div className="absolute top-5 right-5 z-20 flex items-center gap-2 px-3.5 py-1.5 rounded-full backdrop-blur-md"
              style={{ background: "rgba(10,5,0,0.55)", border: "1px solid rgba(255,80,80,0.35)" }}>
              <motion.span animate={{ opacity: [1, 0.2, 1] }} transition={{ duration: 1.4, repeat: Infinity }} className="w-1.5 h-1.5 rounded-full bg-red-500" />
              <span className="text-[10px] font-bold uppercase tracking-[1.5px] text-red-400">Live Replay</span>
            </div>

            {/* play icon, top-left, overlapping video */}
            <div className="absolute top-5 left-5 z-20 w-10 h-10 rounded-[13px] flex items-center justify-center backdrop-blur-md"
              style={{ background: "linear-gradient(135deg,#FF0000,#CC0000)", boxShadow: "0 10px 26px rgba(255,0,0,0.35)" }}>
              <FiPlay size={14} className="text-white" />
            </div>
          </div>
        </div>

        {/* caption below the frame   light, airy, no bulky bar */}
        <div className="flex items-center justify-between mt-6 px-2">
          <div>
            <p className="font-semibold text-[#1F1F1F] text-[17px]" style={{ fontFamily: "Poppins" }}>{title}</p>
            <p className="text-[#888] text-[13px] mt-1">{subtitle}</p>
          </div>
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(242,154,12,0.1)" }}>
            <FiPlay size={14} style={{ color: "#F29A0C" }} />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════
// Compact stat tile   used in the Achievement History grid, with gradient-glow hover
// ══════════════════════════════════════════
function StatTile({ icon: Icon, label, value, color, index }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const qx = gsap.quickTo(el, "rotateY", { duration: 0.5, ease: "power3.out" });
    const qy = gsap.quickTo(el, "rotateX", { duration: 0.5, ease: "power3.out" });
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      qx((((e.clientX - r.left) / r.width) - 0.5) * 18);
      qy((((e.clientY - r.top) / r.height) - 0.5) * -18);
    };
    const onLeave = () => { qx(0); qy(0); };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.55, delay: index * 0.07, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -6 }}
      className="group relative"
      style={{ perspective: "700px" }}
    >
      <div
        ref={ref}
        className="relative overflow-hidden rounded-[20px] border border-[#F4D9B5] bg-white/70 backdrop-blur-xl px-5 py-4 transition-shadow duration-500 hover:shadow-[0_25px_60px_rgba(242,154,12,0.2)]"
        style={{ transformStyle: "preserve-3d", willChange: "transform" }}
      >
        <div className="absolute -inset-[1px] rounded-[20px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{ background: `linear-gradient(135deg, ${color}55, transparent 60%)`, zIndex: -1 }} />
        <div className="w-9 h-9 rounded-[12px] flex items-center justify-center mb-3 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6"
          style={{ background: `${color}1c`, transform: "translateZ(20px)" }}>
          <Icon size={15} style={{ color }} />
        </div>
        <span className="block text-[10px] font-bold uppercase tracking-[1.5px] text-[#B9A98A]" style={{ transform: "translateZ(15px)" }}>{label}</span>
        <p className="text-[#161616] font-semibold text-[14px] mt-1 leading-snug" style={{ transform: "translateZ(15px)" }}>{value}</p>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════
// Infinite GSAP marquee row   used for the country chips (Global Competition section)
// ══════════════════════════════════════════
function MarqueeRow({ items, direction = 1, speed = 40, renderItem }) {
  const trackRef = useRef(null);
  const tweenRef = useRef(null);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const distance = el.scrollWidth / 2;
    tweenRef.current = gsap.fromTo(
      el,
      { x: direction > 0 ? 0 : -distance },
      { x: direction > 0 ? -distance : 0, duration: speed, ease: "none", repeat: -1 }
    );
    return () => tweenRef.current && tweenRef.current.kill();
  }, [direction, speed]);

  return (
    <div
      className="overflow-hidden"
      onMouseEnter={() => tweenRef.current && tweenRef.current.pause()}
      onMouseLeave={() => tweenRef.current && tweenRef.current.resume()}
    >
      <div ref={trackRef} className="flex gap-3 w-max">
        {[...items, ...items].map((item, i) => renderItem(item, i))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════
// Animated flip-in count-up stat card   used in the Global Competition stat row
// ══════════════════════════════════════════
function StatCountCard({ icon: Icon, value, suffix = "", label, color, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, rotateX: -70, y: 30 }}
      whileInView={{ opacity: 1, rotateX: 0, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.8, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -8, scale: 1.03 }}
      style={{ transformPerspective: 800, transformStyle: "preserve-3d" }}
      className="group relative overflow-hidden rounded-[32px] border p-7 py-9 text-center"
    >
      <div className="absolute inset-0" style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.08)" }} />
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 0%, ${color}22, transparent 70%)` }} />
      <div className="absolute top-0 left-0 right-0 h-[2px] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-center" style={{ background: `linear-gradient(90deg,transparent,${color},transparent)` }} />
      <div className="relative z-10">
        <div className="w-12 h-12 rounded-[16px] flex items-center justify-center mx-auto mb-4 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6" style={{ background: `${color}22` }}>
          <Icon size={20} style={{ color }} />
        </div>
        <p className="font-black text-[40px] leading-none" style={{ fontFamily: "Poppins", color }}>
          {typeof value === "number" ? (
            <>
              <CountUp end={value} duration={2.2} enableScrollSpy scrollSpyOnce />
              {suffix}
            </>
          ) : (
            <span>{value}</span>
          )}
        </p>
        <p className="text-[13px] mt-3" style={{ color: "rgba(255,255,255,0.45)" }}>{label}</p>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════
// Advanced gallery card   GSAP cursor tilt, animated conic-gradient border, spotlight,
// diagonal shine sweep, and a caption that slides up from the bottom on hover
// ══════════════════════════════════════════
function GalleryTiltCard({ img, onClick, index }) {
  const cardRef = useRef(null);
  const spotRef = useRef(null);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const qx = gsap.quickTo(el, "rotateY", { duration: 0.5, ease: "power3.out" });
    const qy = gsap.quickTo(el, "rotateX", { duration: 0.5, ease: "power3.out" });
    const qs = gsap.quickTo(el, "scale", { duration: 0.4, ease: "power3.out" });

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      qx((px - 0.5) * 22);
      qy((py - 0.5) * -22);
      qs(1.035);
      if (spotRef.current) {
        spotRef.current.style.background = `radial-gradient(circle at ${px * 100}% ${py * 100}%, rgba(255,209,102,0.45), transparent 45%)`;
      }
    };
    const onLeave = () => { qx(0); qy(0); qs(1); };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.65, delay: (index % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="relative break-inside-avoid mb-6"
    >
      <div
        ref={cardRef}
        onClick={onClick}
        style={{ transformStyle: "preserve-3d", transformPerspective: 1000, willChange: "transform" }}
        className="relative cursor-pointer group"
      >
        {/* animated conic gradient glow ring */}
        <motion.div
          className="absolute -inset-[3px] rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{ background: "conic-gradient(from 0deg, #F29A0C, #FFD166, #6B8A5A, #F29A0C)", filter: "blur(11px)" }}
          animate={{ rotate: 360 }}
          transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
        />

        <div className="relative rounded-[28px] overflow-hidden border-2 border-[#F29A0C]/60 shadow-[0_20px_60px_rgba(0,0,0,0.1)]" style={{ transform: "translateZ(20px)" }}>
          <img
            src={img.src}
            alt={img.label || ""}
            loading="lazy"
            className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.12] group-hover:brightness-[0.8] block"
          />

          {/* cursor-tracked spotlight */}
          <div ref={spotRef} className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none" style={{ mixBlendMode: "overlay" }} />

          {/* diagonal shine sweep */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -inset-y-10 -left-1/2 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-white/35 to-transparent -translate-x-[200%] group-hover:translate-x-[450%] transition-transform duration-[1100ms] ease-out" />
          </div>

          {/* top sweep line */}
          <div className="absolute top-0 left-0 right-0 h-[3px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
            style={{ background: "linear-gradient(90deg,transparent,#F29A0C,#FFD166,transparent)" }} />

          {/* tag ribbon, always visible */}
          {img.tag && (
            <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[1.5px] z-10"
              style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)", color: "#fff", boxShadow: "0 5px 20px rgba(242,154,12,0.5)" }}>
              {img.tag}
            </div>
          )}

          {/* expand icon */}
          <div className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center scale-0 group-hover:scale-100 transition-all duration-500"
            style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.25)" }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1h5M1 1v5M13 13H8M13 13V8M13 1h-5M13 1v5M1 13h5M1 13V8" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>

          {/* caption slides up from below on hover */}
          {img.label && (
            <div className="absolute inset-x-0 bottom-0 p-6 translate-y-[40%] opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 ease-out"
              style={{ background: "linear-gradient(to top,rgba(10,5,0,0.92) 0%,rgba(10,5,0,0.5) 55%,transparent 100%)" }}>
              <p className="font-black text-white text-[16px]" style={{ fontFamily: "Poppins", letterSpacing: "-0.3px" }}>{img.label}</p>
              <p className="text-[12px] mt-1" style={{ color: "rgba(255,255,255,0.55)" }}>Divya Nithin Bhojwani · World Championship 2024</p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════
// Full Gallery section   masonry grid showing every image, advanced tilt-card hover
// ══════════════════════════════════════════
function GallerySection({ onOpen }) {
  return (
    <section className="relative overflow-hidden py-24 px-6 lg:px-20">
      <div className="absolute inset-0 bg-gradient-to-br from-[#FFFDF8] via-[#FFF3E0] to-[#FFF8EE]" />
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 160, ease: "linear" }}
        className="absolute top-[10%] right-[5%] w-[480px] h-[480px] rounded-full border border-[#F29A0C]/10 pointer-events-none" />
      <motion.div animate={{ rotate: -360 }} transition={{ repeat: Infinity, duration: 180, ease: "linear" }}
        className="absolute bottom-[8%] left-[5%] w-[380px] h-[380px] rounded-full border border-[#F29A0C]/10 pointer-events-none" />

      <div className="relative z-10 max-w-[1440px] mx-auto">
        <Reveal>
          <div className="text-center max-w-[700px] mx-auto mb-14">
            <SectionBadge label="CHAMPIONSHIP GALLERY" />
            <h2 className="text-[42px] md:text-[60px] leading-[1.15] tracking-[-1px] font-semibold text-[#111111] mt-6" style={{ fontFamily: "Poppins" }}>
              Moments of <span className="text-[#F29A0C]">Glory</span>
            </h2>
            <p className="text-[#777777] text-[16px] leading-[30px] mt-5">Every pose, every moment, captured on the world stage.</p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="columns-2 lg:columns-3 gap-6">
            {GALLERY.map((img, i) => (
              <GalleryTiltCard key={i} img={img} index={i} onClick={() => onOpen(img)} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const STORY_CHAPTERS = [
  {
    image: "/assets/gallery/image69.jpg",
    number: "01",
    icon: FiAward,
    title: "The Gold Medal Win",
    text: "Gondia's renowned yoga trainer Divya Nithin Bhojwani won Gold at the 1st International World Yogasana Championship, held in Ahmedabad on 6 June 2024, bringing pride to both India and Gondia on the global stage.",
  },
  {
    image: "/assets/gallery/image56.jpg",
    number: "02",
    icon: FiTrendingUp,
    title: "Masters Category Champion",
    text: "Competing in the Masters Category (45–55 years), Divya Nithin Bhojwani secured first place with 35.42 points, outperforming participants from Romania (2nd) and New Zealand (3rd).",
  },
  {
    image: "/assets/gallery/image64.jpg",
    number: "03",
    icon: FiGlobe,
    title: "A Global Stage",
    text: "The championship was organized under Yogasana Bharat, World Yogasana, and the Indian Olympic Association (IOA), and was inaugurated virtually by Prime Minister Narendra Modi.",
  },
];

const ORGANIZERS = ["Yogasana Bharat", "World Yogasana", "Indian Olympic Association (IOA)"];

// ══════════════════════════════════════════
// Editorial chapter row   large gradient number, thumbnail, animated left accent + hover tint
// ══════════════════════════════════════════
function StoryRow({ image, number, icon: Icon, title, text, index, total }) {
  const imgWrapRef = useRef(null);
  const rowRef = useRef(null);

  useEffect(() => {
    const el = imgWrapRef.current;
    if (!el) return;
    const qx = gsap.quickTo(el, "rotateY", { duration: 0.6, ease: "power3.out" });
    const qy = gsap.quickTo(el, "rotateX", { duration: 0.6, ease: "power3.out" });
    const qs = gsap.quickTo(el, "scale", { duration: 0.5, ease: "power3.out" });
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      qx((((e.clientX - r.left) / r.width) - 0.5) * 16);
      qy((((e.clientY - r.top) / r.height) - 0.5) * -16);
      qs(1.04);
    };
    const onLeave = () => { qx(0); qy(0); qs(1); };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  // subtle scroll-linked parallax on the image itself
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        imgWrapRef.current,
        { y: 30 },
        {
          y: -30,
          ease: "none",
          scrollTrigger: {
            trigger: rowRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 0.8,
          },
        }
      );
    }, rowRef);
    return () => ctx.revert();
  }, []);

  return (
    <motion.div
      ref={rowRef}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.65, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="story-row group relative grid grid-cols-1 sm:grid-cols-[70px_1fr_240px] lg:grid-cols-[90px_1fr_300px] gap-6 sm:gap-8 items-center py-10 border-b border-[#EFE1CC] transition-colors duration-500 hover:bg-gradient-to-r hover:from-[#F29A0C]/[0.05] hover:to-transparent px-4 sm:px-6 -mx-4 sm:-mx-6 rounded-[24px] overflow-hidden"
    >
      {/* animated left accent bar */}
      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-0 group-hover:h-[70%] transition-all duration-500 rounded-full"
        style={{ background: "linear-gradient(to bottom,#F29A0C,#FFD166)" }} />

      {/* big gradient number   desktop only */}
      <span
        className="hidden sm:block font-semibold text-[56px] lg:text-[68px] leading-none tracking-[-3px] transition-all duration-500 group-hover:tracking-[-1px]"
        style={{
          fontFamily: "Poppins",
          background: "linear-gradient(135deg,#F29A0C,#FFD166)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          opacity: 0.85,
        }}
      >
        {number}
      </span>

      <div className="order-3 sm:order-2">
        <div className="flex items-center gap-3 mb-3 sm:hidden">
          <span className="font-semibold text-[40px] leading-none tracking-[-2px]"
            style={{ fontFamily: "Poppins", background: "linear-gradient(135deg,#F29A0C,#FFD166)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            {number}
          </span>
        </div>
        <h3 className="text-[#111111] font-semibold text-[21px] sm:text-[23px] lg:text-[25px] transition-transform duration-500 group-hover:translate-x-1" style={{ fontFamily: "Poppins" }}>
          {title}
        </h3>
        <p className="text-[#666666] text-[14.5px] sm:text-[15px] leading-[27px] mt-3 max-w-[520px]">{text}</p>
      </div>

      {/* large image, right side, GSAP cursor tilt + scroll parallax */}
      <div className="order-2 sm:order-3" style={{ perspective: "1200px" }}>
        <div
          ref={imgWrapRef}
          className="relative w-full h-[190px] sm:h-[150px] lg:h-[190px] rounded-[26px] overflow-hidden border border-[#F4D9B5] shadow-[0_20px_50px_rgba(0,0,0,0.1)] transition-shadow duration-500 group-hover:shadow-[0_30px_80px_rgba(242,154,12,0.25)]"
          style={{ transformStyle: "preserve-3d", willChange: "transform" }}
        >
          <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" style={{ transform: "translateZ(10px)" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
          {/* shine sweep */}
          <div className="absolute -inset-y-10 -left-1/2 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-[200%] group-hover:translate-x-[450%] transition-transform duration-[1100ms] ease-out pointer-events-none" />
          <div className="absolute bottom-3 left-3 w-9 h-9 rounded-[12px] flex items-center justify-center text-white shadow-md" style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)", transform: "translateZ(25px)" }}>
            <Icon size={14} />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════
// Full Story section   modern editorial layout: sticky quote panel (left) + scroll-progress
// chapter list (right), gradient mesh background, glassmorphism
// ══════════════════════════════════════════
function FullStorySection() {
  const listRef = useRef(null);
  const progressRef = useRef(null);
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
  const blobY1 = useTransform(scrollYProgress, [0, 1], [-80, 120]);
  const blobY2 = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const headingY = useTransform(scrollYProgress, [0, 0.4], [40, 0]);
  const headingOpacity = useTransform(scrollYProgress, [0, 0.35], [0.3, 1]);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        progressRef.current,
        { scaleY: 0 },
        {
          scaleY: 1,
          transformOrigin: "top",
          ease: "none",
          scrollTrigger: {
            trigger: listRef.current,
            start: "top 65%",
            end: "bottom 75%",
            scrub: 0.6,
          },
        }
      );
    }, listRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative overflow-hidden py-28 px-6 lg:px-20">
      <div className="absolute inset-0 bg-gradient-to-br from-[#FFFDF8] via-[#FFF6E8] to-[#FFFFFF]" />
      {/* gradient mesh blobs   scroll-linked parallax + idle float */}
      <motion.div style={{ y: blobY1 }} className="absolute top-[-10%] right-[-10%] w-[600px] h-[600px] pointer-events-none">
        <motion.div animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 13, repeat: Infinity, ease: "easeInOut" }}
          className="w-full h-full rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.10),transparent_70%)]" />
      </motion.div>
      <motion.div style={{ y: blobY2 }} className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] pointer-events-none">
        <motion.div animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          className="w-full h-full rounded-full bg-[radial-gradient(circle,rgba(107,138,90,0.08),transparent_70%)]" />
      </motion.div>

      <div className="relative z-10 max-w-[1440px] mx-auto">
        <motion.div style={{ y: headingY, opacity: headingOpacity }} className="max-w-[700px] mb-16">
          <SectionBadge label="THE FULL STORY" />
          <h2 className="text-[42px] md:text-[64px] leading-[1.1] tracking-[-1.5px] font-semibold text-[#111111] mt-6" style={{ fontFamily: "Poppins" }}>
            From Gondia to<br /><span className="text-[#F29A0C]">the World Stage</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-[400px_1fr] gap-14 items-start">

          {/* ── LEFT   sticky glass quote panel ── */}
          <Reveal>
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="relative overflow-hidden rounded-[38px] p-9 border"
                style={{ background: "linear-gradient(150deg,#120700,#1A0D00)", borderColor: "rgba(242,154,12,0.2)", boxShadow: "0 40px 100px rgba(0,0,0,0.25)" }}>
                <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: "linear-gradient(90deg,transparent,#F29A0C,#FFD166,transparent)" }} />
                <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle,rgba(242,154,12,0.15),transparent)", filter: "blur(40px)" }} />
                <div className="text-[60px] leading-none mb-2" style={{ color: "rgba(242,154,12,0.2)", fontFamily: "Georgia,serif" }}>"</div>
                <p className="text-[17px] font-semibold leading-[1.8] text-white">
                  She competed against participants from over <span style={{ color: "#FFD166" }}>70 countries</span> and secured
                  India's top position, bringing recognition to <span style={{ color: "#FFD166" }}>Gondia</span> and India.
                </p>
                <div className="flex items-center gap-3 pt-5 mt-6 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)" }}>
                    <FiAward size={16} className="text-white" />
                  </div>
                  <div>
                    <p className="font-black text-white text-[13px]">Divya Nithin Bhojwani</p>
                    <p className="text-[10.5px]" style={{ color: "rgba(255,255,255,0.4)" }}>World Champion · Masters 2024</p>
                  </div>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-[30px] border border-white/70 bg-white/70 backdrop-blur-2xl p-6">
                <p className="text-[10px] font-black uppercase tracking-[2.5px] mb-4 text-[#6B8A5A]">Organized By</p>
                {ORGANIZERS.map((org, i) => (
                  <motion.div key={org} whileHover={{ x: 6 }} className="flex items-center gap-3 py-2.5 border-b last:border-b-0" style={{ borderColor: "rgba(239,229,216,0.7)" }}>
                    <div className="w-7 h-7 rounded-[9px] flex items-center justify-center flex-shrink-0" style={{ background: i % 2 === 0 ? "rgba(242,154,12,0.1)" : "rgba(107,138,90,0.1)" }}>
                      <FiGlobe size={12} style={{ color: i % 2 === 0 ? "#F29A0C" : "#6B8A5A" }} />
                    </div>
                    <span className="font-semibold text-[13px] text-[#1F1F1F]">{org}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* ── RIGHT   editorial chapter list with GSAP scroll-progress rail ── */}
          <div ref={listRef} className="relative">
            <div className="hidden sm:block absolute left-[-2px] top-0 bottom-0 w-[2px] bg-[#F4D9B5]/40">
              <div ref={progressRef} className="absolute inset-0 w-full origin-top" style={{ background: "linear-gradient(to bottom,#F29A0C,#FFD166)" }} />
            </div>

            <div>
              {STORY_CHAPTERS.map((chapter, i) => (
                <StoryRow key={chapter.title} {...chapter} index={i} total={STORY_CHAPTERS.length} />
              ))}
            </div>

            {/* Dignitaries   compact signature strip */}
            <Reveal delay={0.1}>
              <div className="mt-10">
                <p className="text-[10px] font-black uppercase tracking-[2.5px] mb-4 text-[#6B8A5A]">Distinguished Guests</p>
                <div className="flex flex-wrap gap-3">
                  {DIGS.map((d, i) => (
                    <motion.div key={d} whileHover={{ y: -4, scale: 1.04 }}
                      className="flex items-center gap-2.5 px-4 py-2.5 rounded-full border transition-all"
                      style={{ background: "rgba(255,255,255,0.75)", borderColor: "rgba(239,229,216,0.9)" }}>
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-black"
                        style={{ background: i % 2 === 0 ? "linear-gradient(135deg,#F29A0C,#E97818)" : "linear-gradient(135deg,#6B8A5A,#8BA47A)" }}>
                        {d.replace("Dr. ", "")[0]}
                      </div>
                      <span className="font-semibold text-[12.5px] text-[#1F1F1F]">{d}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

// ══════════════════════════════════════════
// Global Competition section   two marquees scrolling opposite directions + count-up stats
// ══════════════════════════════════════════
function NationsSection() {
  const row1 = COUNTRIES.slice(0, 10);
  const row2 = COUNTRIES.slice(10);

  return (
    <section className="relative overflow-hidden py-28 px-6 lg:px-20">
      <div className="absolute inset-0">
        <img src="/assets/image 1.jpeg" alt="" className="w-full h-full object-cover" style={{ filter: "saturate(0.55) brightness(0.5)", transform: "scale(1.05)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(145deg,rgba(10,5,0,0.75) 0%,rgba(18,9,0,0.35) 35%,rgba(18,9,0,0.6) 100%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 0%,rgba(242,154,12,0.18),transparent 60%)" }} />
      </div>
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle,rgba(242,154,12,0.09) 1px,transparent 1px)", backgroundSize: "44px 44px", opacity: 0.5 }} />

      <div className="relative z-10 max-w-[1300px] mx-auto">
        <Reveal>
          <div className="text-center max-w-[600px] mx-auto mb-16">
            <SectionBadge label="GLOBAL COMPETITION" dark />
            <h2 className="text-[42px] md:text-[62px] leading-[1.15] tracking-[-1px] font-semibold text-white mt-6" style={{ fontFamily: "Poppins" }}>
              70+ Nations.<br /><span className="text-[#F29A0C]">One Champion.</span>
            </h2>
            <p className="text-[16px] mt-4" style={{ color: "rgba(255,255,255,0.45)", lineHeight: 1.85 }}>
              The world's best yoga practitioners gathered, and India stood tallest.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="space-y-4 mb-16 [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
            <MarqueeRow
              items={row1}
              direction={1}
              speed={38}
              renderItem={(country, i) => {
                const isIndia = country.includes("India");
                return (
                  <div key={i}
                    className="px-5 py-2.5 rounded-full whitespace-nowrap"
                    style={{
                      background: isIndia ? "linear-gradient(135deg,rgba(242,154,12,0.3),rgba(233,120,24,0.2))" : "rgba(255,255,255,0.06)",
                      border: isIndia ? "1px solid rgba(242,154,12,0.55)" : "1px solid rgba(255,255,255,0.1)",
                      color: isIndia ? "#FFD166" : "rgba(255,255,255,0.58)",
                      fontWeight: isIndia ? 800 : 500, fontSize: 13,
                      boxShadow: isIndia ? "0 0 30px rgba(242,154,12,0.25)" : "none",
                    }}>
                    {isIndia ? "🇮🇳 India 🥇" : country}
                  </div>
                );
              }}
            />
            <MarqueeRow
              items={row2}
              direction={-1}
              speed={34}
              renderItem={(country, i) => (
                <div key={i} className="px-5 py-2.5 rounded-full whitespace-nowrap"
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.58)", fontWeight: 500, fontSize: 13 }}>
                  {country}
                </div>
              )}
            />
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <StatCountCard icon={FiUsers} value={70} suffix="+" label="Nations Participated" color="#F29A0C" index={0} />
            <StatCountCard icon={FiAward} value="Gold" label="Medal for India" color="#6B8A5A" index={1} />
            <StatCountCard icon={FiShield} value="#1" label="India's Rank · Masters" color="#F29A0C" index={2} />
          </div>
        </Reveal>
      </div>
    </section>
  );
}




// Smooth scroll reveal   wraps any section content
const Reveal = ({ children, delay = 0, y = 40, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y, filter: "blur(8px)" }}
    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1], delay }}
    style={{ willChange: "transform, opacity, filter" }}
    className={className}>
    {children}
  </motion.div>
);

const SectionBadge = ({ label, dark = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }}
    className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/20 mt-8">
    <div className="w-2 h-2 rounded-full bg-[#F29A0C]" />
    <span className={`tracking-[3px] uppercase text-xs font-semibold ${dark ? "text-white" : "text-[#F29A0C]"}`}>{label}</span>
  </motion.div>
);

const PhilosophyCard = ({ image, badge, icon: Icon, number, title, items, text }) => (
  <motion.div
    whileHover={{ y: -10, scale: 1.01 }} transition={{ duration: 0.4 }}
    style={{ willChange: "transform" }}
    className="group relative overflow-hidden rounded-[48px] border border-[#F4D9B5] bg-white/65 backdrop-blur-2xl min-h-[720px] shadow-[0_40px_140px_rgba(0,0,0,0.06)]">
    <div className="relative h-[300px] overflow-hidden">
      <img src={image} alt="" className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105" style={{ willChange: "transform" }} loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-transparent" />
      <motion.div animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 5 }}
        className="absolute left-8 top-8 rounded-full border border-white/20 bg-white/10 backdrop-blur-2xl px-6 py-2">
        <span className="text-white text-xs tracking-[4px] uppercase font-semibold">{badge}</span>
      </motion.div>
      <div className="absolute bottom-[40px] left-10 w-20 h-20 rounded-[32px] bg-gradient-to-br from-[#F29A0C] to-[#FF7A00] flex items-center justify-center text-white text-4xl shadow-[0_30px_80px_rgba(242,154,12,0.30)]">
        <Icon />
      </div>
    </div>
    <div className="relative px-10 py-8">
      <span className="absolute top-6 right-10 text-[#F29A0C] text-[110px] font-semibold leading-none opacity-[0.06]">{number}</span>
      <h3 className="text-[48px] md:text-[58px] leading-[0.92] tracking-[-4px] font-semibold text-[#111111]" style={{ fontFamily: "Poppins", whiteSpace: "pre-line" }}>{title}</h3>
      <div className="w-24 h-[2px] bg-gradient-to-r from-[#F29A0C] to-transparent mt-8" />
      <p className="text-[#555555] text-[17px] leading-[38px] mt-8">{text}</p>
      <div className="space-y-5 mt-12">
        {items.map((item, i) => (
          <motion.div key={i} whileHover={{ x: 6 }} className="flex items-center gap-4">
            <div className="w-3 h-3 rounded-full bg-[#F29A0C]" />
            <span className="text-[#222222] text-[15px] font-medium">{item}</span>
          </motion.div>
        ))}
      </div>
    </div>
  </motion.div>
);

const About = () => {
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const smoothRotateX = useSpring(rotateX, { stiffness: 400, damping: 30, mass: 0.5 });
  const smoothRotateY = useSpring(rotateY, { stiffness: 400, damping: 30, mass: 0.5 });

  const [isMobile, setIsMobile] = useState(true);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      const timer = setTimeout(() => setShouldLoadVideo(true), 1200);
      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  // GSAP scroll-triggered stagger reveal for the "What Is Divyyog Foundation" pillar cards
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".pillar-reveal", {
        opacity: 0,
        y: 60,
        rotateX: -15,
        transformOrigin: "top center",
        stagger: 0.15,
        duration: 0.9,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".whatis-section",
          start: "top 75%",
          once: true,
        },
      });
    });
    return () => ctx.revert();
  }, []);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    rotateX.set(((e.clientY - rect.top) / rect.height - 0.5) * -12);
    rotateY.set(((e.clientX - rect.left) / rect.width - 0.5) * 12);
  };

  // Seed particles once to prevent recalculating coordinates and glitching on re-renders
  const particles = useMemo(() => {
    return Array.from({ length: 14 }).map((_, i) => ({
      id: i,
      width: `${(i % 3) + 4}px`,
      height: `${(i % 3) + 4}px`,
      top: `${((i * 17) % 80) + 10}%`,
      left: `${((i * 23) % 80) + 10}%`,
      duration: `${6 + (i % 5)}s`,
      delay: `${i * 0.3}s`,
    }));
  }, []);

  return (
    <div className="relative overflow-hidden bg-[#FFF9F2]">
      {/* Dynamic Keyframes injected locally to ensure 60fps animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes float-particle {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.2; }
          50% { transform: translateY(-40px) translateX(15px); opacity: 0.8; }
        }
        .animate-float-particle {
          animation: float-particle var(--duration) ease-in-out infinite;
        }
      `}} />

      {/* ── HERO ── */}
      <section className="relative min-h-screen overflow-hidden bg-[#0A0400]">
        {/* Base Hero Background Layer */}
        <div className="absolute inset-0 w-full h-full">
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-[center_20%] sm:bg-center" 
            style={{ backgroundImage: "url('/assets/about_hero_bg.jpg')" }} 
          />
          {/* Subtle dark brown overlay on background image */}
          <div className="absolute inset-0 bg-[#120700]/40 pointer-events-none" />
        </div>
        {!isMobile && shouldLoadVideo && (
          <video autoPlay muted loop playsInline preload="auto"
            poster="/assets/about_hero_bg.jpg"
            className="absolute inset-0 w-full h-full object-cover scale-[1.05]"
            style={{ willChange: "transform", transform: "translateZ(0)", backfaceVisibility: "hidden" }}>
            <source src="/assets/video7.mp4" type="video/mp4" />
          </video>
        )}
        {isMobile && (
          <div className="absolute inset-0 w-full h-full">
            <div 
              className="absolute inset-0 w-full h-full object-cover bg-cover bg-[center_20%] sm:bg-center" 
              style={{ backgroundImage: "url('/assets/about_hero_bg.jpg')" }} 
            />
            {/* Subtle dark brown overlay on mobile background image */}
            <div className="absolute inset-0 bg-[#120700]/40 pointer-events-none" />
          </div>
        )}
        {/* Rich Royal Backdrop Gradients & Overlays (Balanced opacity for artwork visibility) */}
        <div className="absolute inset-0 z-10 bg-[#120700]/20 pointer-events-none" />
        <div className="absolute inset-0 z-10 bg-gradient-to-b from-[#120700]/70 via-[#180902]/45 to-[#0A0400]/75 lg:bg-gradient-to-r lg:from-[#120700]/75 lg:via-[#180902]/45 lg:to-[#120700]/15 pointer-events-none" />
        <div className="absolute inset-0 z-10 bg-[radial-gradient(ellipse_at_top,rgba(242,154,12,0.18)_0%,transparent_65%)] pointer-events-none" />
        <div className="absolute inset-0 z-10 bg-[radial-gradient(circle_at_bottom_left,rgba(242,154,12,0.12)_0%,transparent_50%)] pointer-events-none" />
        <motion.div animate={{ opacity: [0.12, 0.24, 0.12] }} transition={{ repeat: Infinity, duration: 10 }}
          className="absolute top-[-260px] right-[-180px] w-[850px] h-[850px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.2)_0%,transparent_70%)] pointer-events-none will-change-opacity" />
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 120, ease: "linear" }}
          className="absolute right-[6%] top-[8%] w-[520px] h-[520px] rounded-full border border-[#F29A0C]/10 pointer-events-none" />

        <div className="relative z-20 min-h-screen flex items-center py-12 lg:py-14">
          <div className="max-w-[1600px] mx-auto px-5 sm:px-6 lg:px-20 w-full">
            <Reveal className="max-w-[820px] pt-16 sm:pt-[100px]">
              <div className="inline-flex items-center gap-2.5 sm:gap-3 rounded-full border border-white/40 bg-white/20 backdrop-blur-2xl px-4 sm:px-6 py-2 sm:py-3">
                <div className="w-2 h-2 rounded-full bg-[#F29A0C]" />
                <span className="text-[9px] sm:text-[10px] tracking-[3px] sm:tracking-[4px] uppercase font-semibold text-white">ABOUT DIVYYOG FOUNDATION</span>
              </div>
              <h1 className="text-[36px] sm:text-[50px] md:text-[66px] lg:text-[68px] leading-[1.04] sm:leading-[1.02] tracking-[0.5px] sm:tracking-[1px] font-semibold text-white mt-6 sm:mt-8"
                style={{ fontFamily: "Poppins" }}>
                Transforming<br />Wellness Through
                <span className="text-[#F29A0C]"> Authentic Yoga</span>
              </h1>
              <p className="text-white text-sm sm:text-[17px] md:text-[19px] leading-[28px] sm:leading-[38px] mt-6 sm:mt-8 max-w-[680px]">
                Divyyog Foundation is a premium global wellness platform rooted in authentic yogic traditions.
                Guided by compassion, mindfulness, and spiritual discipline, we empower students worldwide to transform their lives through yoga.
              </p>
              <div className="flex flex-wrap gap-5 mt-6">
                <Link to="/courses">
                  <motion.button whileHover={{ y: -4, scale: 1.03 }}
                    className="group relative overflow-hidden rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF7A00] text-white px-8 py-4 text-[15px] font-semibold shadow-[0_25px_80px_rgba(242,154,12,0.28)] cursor-pointer">
                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-all duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" />
                    <span className="relative z-20 flex items-center gap-3">Explore Programs<FaArrowRight /></span>
                  </motion.button>
                </Link>
                <Link to="/trainers">
                  <motion.button whileHover={{ y: -4, scale: 1.03 }}
                    className="rounded-full border border-white/40 bg-white/20 backdrop-blur-2xl text-white px-8 py-4 text-[15px] font-medium hover:bg-white/30 hover:border-white/60 transition-all cursor-pointer">
                    Meet Our Trainers
                  </motion.button>
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>


      {/* ── WHAT IS DIVYYOG FOUNDATION ── */}
      <section id="what-is-divyyog" className="whatis-section relative overflow-hidden py-28">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFFDF8] via-[#FFF8EE] to-[#FFFFFF]" />
        <motion.div animate={{ scale: [1, 1.06, 1] }} transition={{ repeat: Infinity, duration: 13 }}
          className="absolute top-[-220px] right-[-200px] w-[780px] h-[780px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.08)_0%,transparent_70%)] pointer-events-none will-change-transform" style={{ transform: "translateZ(0)" }} />
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay bg-[url('https://www.transparenttextures.com/patterns/asfalt-light.png')]" />

        <div className="relative z-20 max-w-[1650px] mx-auto px-6 lg:px-20">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-16 items-center">
            <Reveal>
              <SectionBadge label="WHO WE ARE" />
              <h2 className="text-[42px] md:text-[58px] leading-[1.15] tracking-[-1px] font-semibold text-[#111111] mt-6" style={{ fontFamily: "Poppins" }}>
                What Is <span className="text-[#F29A0C]">Divyyog Foundation?</span>
              </h2>
              <p className="text-[#555555] text-[17px] leading-[38px] mt-8 max-w-[620px]">
                Divyyog Foundation is a premier global yoga and wellness institution dedicated to
                sharing authentic yogic wisdom with the modern world. We harmonize classical Vedic
                lineages with scientific biomechanical precision so practitioners everywhere can build a
                sustainable, life-transforming sadhana.
              </p>
              <p className="text-[#666666] text-[16px] leading-[32px] mt-5 max-w-[620px]">
                From interactive live masterclasses to immersive retreats, our curriculum is anchored
                in three enduring pillars: spiritual authenticity, holistic accessibility, and profound personal transformation.
              </p>
              <div className="mt-10">
                <Link to="/courses">
                  <MagneticButton className="group relative overflow-hidden rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF7A00] text-white px-8 py-4 text-[14px] font-semibold shadow-[0_20px_60px_rgba(242,154,12,0.25)] inline-flex items-center gap-3 cursor-pointer">
                    <span className="relative z-20">Learn More About Us</span>
                    <FaArrowRight className="relative z-20" />
                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-all duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" />
                  </MagneticButton>
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="grid sm:grid-cols-2 gap-5">
                {FOUNDATION_PILLARS.map((p, i) => (
                  <div key={p.title} className="pillar-reveal">
                    <PillarCard icon={p.icon} title={p.title} text={p.text} index={i} />
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── FOUNDER ── */}
      <section id="founder" className="relative overflow-hidden py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFFDF8] via-[#FFF8EE] to-[#FFFFFF]" />
        <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ repeat: Infinity, duration: 12 }}
          className="absolute top-[-240px] left-[-220px] w-[850px] h-[850px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.08)_0%,transparent_70%)] pointer-events-none will-change-transform" style={{ transform: "translateZ(0)" }} />
        <div className="relative z-20 max-w-[1600px] mx-auto px-6 lg:px-20">
          <Reveal>
            <div className="grid lg:grid-cols-[520px_1fr] gap-24 items-center">
              <div className="relative">
                <div className="relative overflow-hidden rounded-[42px] h-[700px] border border-[#F4D9B5] shadow-[0_40px_140px_rgba(0,0,0,0.08)]">
                  <img src="/assets/Divya_1.png" alt="Mrs. Divya Nithin Bhojwani" className="w-full h-full object-cover object-top" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/5 to-transparent" />
                </div>
                <motion.div animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 5 }}
                  style={{ willChange: "transform" }}
                  className="absolute bottom-4 right-4 lg:bottom-[-40px] lg:right-[-20px] rounded-[32px] border border-white/30 bg-white/75 backdrop-blur-2xl p-8 shadow-[0_30px_100px_rgba(0,0,0,0.08)]">
                  <span className="text-[#F29A0C] uppercase tracking-[4px] text-xs font-semibold">EXPERIENCE</span>
                  <h3 className="text-[52px] leading-none tracking-[-3px] font-semibold text-[#111111] mt-4">1000+</h3>
                  <p className="text-[#666666] text-sm mt-3">Students Trained Globally</p>
                </motion.div>
              </div>
              <div>
                <SectionBadge label="MEET YOUR GUIDE" />
                <h2 className="text-[42px] md:text-[58px] leading-[1.2] tracking-[-1px] font-semibold text-[#111111] mt-6" style={{ fontFamily: "Poppins" }}>
                  Mrs. Divya Nithin<br />Bhojwani
                </h2>
                <p className="text-[#555555] text-[17px] md:text-[19px] leading-[38px] mt-10">
                  Mrs. Divya Nithin Bhojwani is the visionary founder of Divyyog Foundation and an International Gold Medalist in Yoga.<br /><br />
                  Through her compassionate teaching and deep spiritual discipline, she has transformed the lives of students across India and globally.
                  Her philosophy blends classical yogic wisdom with a modern, accessible approach to wellness.
                </p>
                <div className="grid md:grid-cols-2 gap-5 mt-14">
                  {specializations.map((item, i) => (
                    <motion.div key={i} whileHover={{ y: -4 }}
                      style={{ willChange: "transform" }}
                      className="rounded-[28px] border border-[#F4D9B5] bg-white/70 backdrop-blur-2xl p-6 shadow-[0_15px_60px_rgba(0,0,0,0.04)]">
                      <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-2xl bg-[#F29A0C]/10 flex items-center justify-center text-[#F29A0C]"><FaLeaf /></div>
                        <span className="text-[#222222] text-[15px] font-medium">{item}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      {/* ══════════════════════════════════════════
          ACHIEVEMENT SUMMARY   redesigned: video full-width, then
          two columns   Achievement History (left) + Podium (right)
      ══════════════════════════════════════════ */}
      <section className="achievement-section relative overflow-hidden py-28 px-6 lg:px-20">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF8EE] via-[#FFF3E0] to-[#FFFDF8]" />
        <motion.div animate={{ y: [0, -25, 0] }} transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-0 right-0 w-[550px] h-[550px] rounded-full pointer-events-none bg-[radial-gradient(circle,rgba(242,154,12,0.08),transparent)]" />
        <motion.div animate={{ y: [0, 20, 0] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none bg-[radial-gradient(circle,rgba(107,138,90,0.06),transparent)]" />

        <div className="relative z-10 max-w-[1440px] mx-auto">
          <Reveal>
            <div className="text-center max-w-[800px] mx-auto mb-16">
              <SectionBadge label="ACHIEVEMENT SUMMARY" />
              <h2 className="text-[42px] md:text-[60px] leading-[1.15] tracking-[-1px] font-semibold text-[#111111] mt-6" style={{ fontFamily: "Poppins" }}>
                A Historic Victory<br /><span className="text-[#F29A0C]">for India</span>
              </h2>
            </div>
          </Reveal>

          {/* ── Full-width video on top ── */}
          <Reveal delay={0.1}>
            <VideoShowcase
              embedUrl="https://www.youtube.com/embed/0H67f5VFGio?si=psTtd1tFmSgXX9ek"
              title="Championship Highlights"
              subtitle="World Yogasana Championship · Ahmedabad 2024"
            />
          </Reveal>

          {/* ── Below the video: two columns ── */}
          <div className="grid lg:grid-cols-2 gap-10 items-start mt-14">

            {/* LEFT   Achievement History (timeline) */}
            <Reveal delay={0.15}>
              <TiltCard className="relative overflow-hidden rounded-[42px] border border-white/70 bg-white/60 backdrop-blur-3xl shadow-[0_40px_120px_rgba(242,154,12,0.08)] p-8 lg:p-10 h-full">
                <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: "linear-gradient(90deg,transparent,#F29A0C,#FFD166,#F29A0C,transparent)" }} />
                <div className="flex items-center gap-5 mb-8 pb-8 border-b border-[#EFE5D8]">
                  <motion.div animate={{ rotate: [0, 6, -6, 0] }} transition={{ duration: 5, repeat: Infinity }}
                    className="relative w-[74px] h-[74px] rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: "conic-gradient(from 0deg,#F29A0C,#FFD166,#E97818,#FFD166,#F29A0C)", boxShadow: "0 0 46px rgba(242,154,12,0.55), 0 0 0 4px rgba(242,154,12,0.14)" }}>
                    <div className="w-[58px] h-[58px] rounded-full flex items-center justify-center bg-[#FFF8EE]">
                      <span style={{ fontSize: "30px" }}>🥇</span>
                    </div>
                  </motion.div>
                  <div>
                    <h3 className="font-semibold text-[#1F1F1F] text-[21px]" style={{ fontFamily: "Poppins" }}>Achievement History</h3>
                    <p className="text-[13px] font-semibold mt-1" style={{ color: "#6B8A5A" }}>Divya Nithin Bhojwani · Gold Medalist</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: FiAward, label: "Event", value: "World Yogasana Championship", color: "#F29A0C" },
                    { icon: FiCalendar, label: "Date", value: "6 June 2024", color: "#6B8A5A" },
                    { icon: FiMapPin, label: "Venue", value: "Ahmedabad, India", color: "#F29A0C" },
                    { icon: FiStar, label: "Category", value: "Masters 45–55", color: "#6B8A5A" },
                    { icon: FiTrendingUp, label: "Score", value: "35.42 Points", color: "#F29A0C" },
                    { icon: FiAward, label: "Result", value: "Gold · 1st Place", color: "#6B8A5A" },
                  ].map((item, i) => (
                    <StatTile key={item.label} {...item} index={i} />
                  ))}
                </div>

                <div className="mt-6 rounded-[20px] p-5 flex items-center gap-4" style={{ background: "linear-gradient(135deg,rgba(242,154,12,0.08),rgba(107,138,90,0.05))", border: "1px solid rgba(242,154,12,0.15)" }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(242,154,12,0.15)" }}>
                    <FiGlobe size={16} style={{ color: "#F29A0C" }} />
                  </div>
                  <p className="text-[13px] leading-[24px]" style={{ color: "#555" }}>
                    <strong style={{ color: "#1F1F1F" }}>Represented India 🇮🇳</strong> — competed against 70+ countries and secured
                    the top position, bringing recognition to Gondia and India.
                  </p>
                </div>
              </TiltCard>
            </Reveal>

            {/* RIGHT   Podium */}
            <Reveal delay={0.25}>
              <MagCard className="relative overflow-hidden rounded-[42px] border border-white/80 bg-white/70 backdrop-blur-3xl shadow-[0_30px_90px_rgba(242,154,12,0.08)] hover:shadow-[0_45px_110px_rgba(242,154,12,0.15)] transition-shadow duration-700 h-full flex flex-col">
                <div className="absolute top-0 left-0 right-0 h-[4px]" style={{ background: "linear-gradient(90deg, transparent, #F29A0C, #FFD166, #F29A0C, transparent)" }} />
                <div className="absolute -right-24 -top-24 w-56 h-56 rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.15)_0%,transparent_70%)] blur-2xl pointer-events-none" />
                <div className="absolute -left-24 -bottom-24 w-56 h-56 rounded-full bg-[radial-gradient(circle,rgba(107,138,90,0.1)_0%,transparent_70%)] blur-2xl pointer-events-none" />

                <div className="p-8 lg:p-12 relative flex-1 flex flex-col justify-center" style={{ transformStyle: "preserve-3d" }}>
                  <p className="text-[11px] font-black uppercase tracking-[3px] mb-4 text-[#E97818]" style={{ transform: "translateZ(15px)" }}>
                    Championship Results · Masters Category (45–55 Years)
                  </p>

                  <div className="relative mt-20 mb-6" style={{ transformStyle: "preserve-3d" }}>
                    <div className="flex items-end justify-center gap-4 sm:gap-6 relative z-20" style={{ transformStyle: "preserve-3d" }}>
                      {[
                        { pos: "2nd", country: "Romania", flagUrl: "https://flagcdn.com/w80/ro.png", color: "#B8B8B8", h: 165, pts: "34.15 pts" },
                        { pos: "1st", country: "India", flagUrl: "https://flagcdn.com/w80/in.png", color: "#F29A0C", h: 215, pts: "35.42 pts", isWinner: true },
                        { pos: "3rd", country: "New Zealand", flagUrl: "https://flagcdn.com/w80/nz.png", color: "#CD7F32", h: 140, pts: "33.80 pts" },
                      ].map((p, i) => (
                        <motion.div key={p.pos}
                          initial={{ opacity: 0, y: 40 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                          className="flex-1 flex flex-col items-center relative pt-10"
                          style={{ transformStyle: "preserve-3d" }}
                        >
                          {p.isWinner && (
                            <motion.div
                              animate={{ y: [0, -6, 0] }}
                              transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                              className="absolute -top-10 z-30"
                              style={{ transform: "translateZ(60px)" }}
                            >
                              <FaCrown size={60} className="text-[#FFD166] drop-shadow-[0_4px_10px_rgba(242,154,12,0.4)]" />
                            </motion.div>
                          )}

                          <div className="relative z-20 flex items-center justify-center -mb-5" style={{ transform: "translateZ(45px)", transformStyle: "preserve-3d" }}>
                            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center p-1 bg-white/95 border-2 shadow-[0_8px_20px_rgba(0,0,0,0.15)] transition-all duration-300"
                              style={{ borderColor: p.color, boxShadow: `0 0 15px ${p.color}40` }}>
                              <img src={p.flagUrl} alt={p.country} className="w-full h-full object-cover rounded-full" />
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black text-white shadow-md"
                              style={{ background: `linear-gradient(135deg, ${p.color}, ${p.color}cc)` }}>
                              {p.pos.slice(0, 1)}
                            </div>
                          </div>

                          <div className="w-full rounded-b-[24px] rounded-t-[14px] flex flex-col items-center justify-between pb-4 pt-7 relative overflow-hidden"
                            style={{
                              height: `${p.h}px`,
                              background: p.isWinner ? "linear-gradient(180deg, rgba(242,154,12,0.18) 0%, rgba(255,255,255,0.03) 100%)" : `linear-gradient(180deg, ${p.color}15 0%, rgba(255,255,255,0.02) 100%)`,
                              border: `1px solid ${p.color}40`,
                              boxShadow: p.isWinner ? "0 20px 45px rgba(242,154,12,0.15), inset 0 1.5px 0 rgba(255,255,255,0.3)" : `0 10px 30px ${p.color}08, inset 0 1px 0 rgba(255,255,255,0.25)`,
                              transform: "translateZ(20px)",
                              transformStyle: "preserve-3d"
                            }}>
                            <div className="absolute top-0 left-0 right-0 h-1 rounded-t-[14px]" style={{ background: p.color }} />
                            <p className="text-[11px] sm:text-[12px] font-bold text-center text-[#555] tracking-tight mt-1" style={{ transform: "translateZ(25px)" }}>{p.country}</p>
                            <span className="font-black text-[22px] tracking-tight leading-none my-1"
                              style={{ color: p.color, textShadow: p.isWinner ? "0 0 12px rgba(242,154,12,0.4)" : "none", fontFamily: "Poppins", transform: "translateZ(35px)" }}>
                              {p.pos}
                            </span>
                            <span className="text-[9px] font-black tracking-wider px-2.5 py-0.5 rounded-full text-white/95 bg-black/30" style={{ transform: "translateZ(30px)" }}>
                              {p.pts.split(" ")[0]}
                            </span>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <div className="w-full h-3.5 rounded-full border border-white/40 backdrop-blur-md mt-[-8px] z-10 relative overflow-hidden"
                      style={{ background: "linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(242,154,12,0.2) 50%, rgba(255,255,255,0.05) 100%)", boxShadow: "0 10px 30px rgba(242,154,12,0.15), inset 0 1px 0 rgba(255,255,255,0.3)", transform: "translateZ(10px)" }}>
                      <div className="absolute inset-x-12 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-[#F29A0C]/50 to-transparent" />
                    </div>
                  </div>

                  <div className="rounded-[22px] px-5 py-4 flex items-center justify-between mt-10 relative overflow-hidden"
                    style={{ background: "linear-gradient(90deg, rgba(242,154,12,0.09) 0%, rgba(255,209,102,0.03) 100%)", border: "1px solid rgba(242,154,12,0.2)", transform: "translateZ(15px)" }}>
                    <div className="flex items-center gap-3">
                      <img src="https://flagcdn.com/w40/in.png" alt="India" className="w-7 h-5 object-cover rounded-[3px] border border-white/50 shadow-sm" />
                      <span className="text-[14px] font-black text-[#1F1F1F]">Divya Nithin Bhojwani · Gold Medalist</span>
                    </div>
                    <span className="font-black text-[13px] px-4 py-2 rounded-full text-white tracking-wider uppercase"
                      style={{ background: "linear-gradient(135deg, #F29A0C, #E97818)", boxShadow: "0 6px 20px rgba(242,154,12,0.4)" }}>
                      35.42 pts
                    </span>
                  </div>
                </div>
              </MagCard>
            </Reveal>
          </div>

          {/* ── Social share row, full width below both columns ── */}
          <Reveal delay={0.3}>
            <div className="relative overflow-hidden rounded-[32px] border border-white/70 bg-white/65 backdrop-blur-2xl px-7 py-6 shadow-[0_20px_70px_rgba(0,0,0,0.05)] flex items-center justify-between flex-wrap gap-4 mt-10">
              <div>
                <p className="font-black text-[#1F1F1F] text-[14px]" style={{ fontFamily: "Poppins" }}>Share This Achievement</p>
                <p className="text-[12px] mt-1" style={{ color: "#999" }}>Follow along and celebrate with us</p>
              </div>
              <div className="flex items-center gap-3">
                {SOCIAL_LINKS.map(({ Icon, label, href, background, shadow, color }) => (
                  <MagneticButton
                    key={label}
                    as="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="relative w-11 h-11 rounded-full flex items-center justify-center text-white overflow-hidden transition-transform duration-300 hover:scale-110"
                    style={{
                      background: background || `linear-gradient(135deg, ${color}, ${color}cc)`,
                      boxShadow: shadow ? `0 10px 26px ${shadow}` : `0 10px 26px ${color}40`,
                    }}
                  >
                    <Icon size={16} />
                  </MagneticButton>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          GALLERY   redesigned: horizontal scroll-snap track of 3D flip cards
      ══════════════════════════════════════════ */}
      <GallerySection onOpen={(img) => setLightbox(img)} />

      {/* ══════════════════════════════════════════
          THE FULL STORY   redesigned: alternating timeline, animated connecting line
      ══════════════════════════════════════════ */}
      <FullStorySection />

      {/* ══════════════════════════════════════════
          GLOBAL COMPETITION   redesigned: dual-direction marquee + count-up stat cards
      ══════════════════════════════════════════ */}
      <NationsSection />

      {/* ── PHILOSOPHY ── */}
      <section className="relative overflow-hidden py-16">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF8EE] via-[#FFE2B6] to-[#FFFDF8]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,154,12,0.18),transparent_28%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(242,154,12,0.14),transparent_30%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.35),transparent_45%)]" />
        <motion.div animate={{ scale: [1, 1.08, 1], opacity: [0.12, 0.22, 0.12] }} transition={{ repeat: Infinity, duration: 12 }}
          className="absolute top-[-280px] left-[-180px] w-[850px] h-[850px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.15)_0%,transparent_70%)] pointer-events-none will-change-transform" style={{ transform: "translateZ(0)" }} />
        <motion.div animate={{ scale: [1, 1.06, 1], opacity: [0.08, 0.15, 0.08] }} transition={{ repeat: Infinity, duration: 14 }}
          className="absolute bottom-[-260px] right-[-180px] w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.4)_0%,transparent_70%)] pointer-events-none will-change-transform" style={{ transform: "translateZ(0)" }} />
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay bg-[url('https://www.transparenttextures.com/patterns/asfalt-light.png')]" />
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 140, ease: "linear" }}
          style={{ willChange: "transform" }}
          className="absolute top-[10%] right-[5%] w-[520px] h-[520px] rounded-full border border-[#F29A0C]/10 pointer-events-none" />
        <motion.div animate={{ rotate: -360 }} transition={{ repeat: Infinity, duration: 160, ease: "linear" }}
          style={{ willChange: "transform" }}
          className="absolute bottom-[8%] left-[5%] w-[420px] h-[420px] rounded-full border border-[#F29A0C]/10 pointer-events-none" />

        <div className="relative z-20 max-w-[1650px] mx-auto px-6 lg:px-20">
          <Reveal>
            <div className="text-center max-w-[850px] mx-auto">
              <SectionBadge label="OUR PHILOSOPHY" />
              <h2 className="text-[46px] md:text-[64px] leading-[1.2] tracking-[-1px] font-semibold text-[#111111] mt-6" style={{ fontFamily: "Poppins" }}>
                Building A Global<br />Wellness Ecosystem
              </h2>
              <p className="text-[#666666] text-[17px] md:text-[19px] leading-[38px] mt-8 max-w-[760px] mx-auto">
                Rooted in authentic yogic traditions and modern wellness philosophy, Divyyog Foundation empowers individuals worldwide through transformative yoga experiences.
              </p>
            </div>
          </Reveal>
          
          <Reveal delay={0.15}>
            <div className="relative mt-16">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[380px] h-[380px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.1)_0%,transparent_70%)] pointer-events-none" />
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <PhilosophyCard
                  image="/assets/image 2.jpeg" badge="MISSION" icon={FaHeart} number="01"
                  title={"Our\nMission"}
                  text="To make authentic yoga accessible globally through compassionate teaching, spiritual discipline, and transformative wellness experiences that empower individuals of all ages and backgrounds."
                  items={["Authentic Yogic Teachings", "Holistic Wellness Transformation", "Accessible Global Learning"]} />
                <PhilosophyCard
                  image="/assets/image 28.jpeg" badge="VISION" icon={FaGlobeAsia} number="02"
                  title={"Our\nVision"}
                  text="To build a global wellness ecosystem where yoga becomes a lifelong practice for physical vitality, emotional balance, mindfulness, inner peace, and spiritual growth."
                  items={["Global Wellness Community", "Mindful Living & Balance", "Spiritual Growth & Inner Peace"]} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="relative overflow-hidden py-16">
        <div className="absolute inset-0">
          <img src="/assets/image 11.jpeg" alt="" className="w-full h-full object-cover scale-[1.08]" />
        </div>
        <div className="absolute inset-0 bg-[#120700]/70" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#F29A0C]/25 via-[#D77C00]/45 to-[#120700]/65" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.10),transparent_30%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(242,154,12,0.18),transparent_35%)]" />
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay bg-[url('/assets/image 11.jpeg')]" />
        <motion.div animate={{ scale: [1, 1.08, 1], opacity: [0.12, 0.22, 0.12] }} transition={{ repeat: Infinity, duration: 10 }}
          className="absolute top-[-220px] left-[-180px] w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.15)_0%,transparent_70%)] pointer-events-none will-change-transform" style={{ transform: "translateZ(0)" }} />
        <motion.div animate={{ scale: [1, 1.05, 1], opacity: [0.08, 0.14, 0.08] }} transition={{ repeat: Infinity, duration: 12 }}
          className="absolute bottom-[-220px] right-[-180px] w-[650px] h-[650px] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.15)_0%,transparent_70%)] pointer-events-none will-change-transform" style={{ transform: "translateZ(0)" }} />
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 160, ease: "linear" }}
          style={{ willChange: "transform" }}
          className="absolute top-[10%] right-[5%] w-[520px] h-[520px] rounded-full border border-white/10 pointer-events-none" />
        
        {/* Hardware-accelerated CSS floating particles */}
        {particles.map((p) => (
          <div key={p.id}
            className="absolute rounded-full bg-white/20 animate-float-particle pointer-events-none"
            style={{
              width: p.width,
              height: p.height,
              top: p.top,
              left: p.left,
              "--duration": p.duration,
              animationDelay: p.delay,
              willChange: "transform, opacity",
            }} />
        ))}

        <div className="relative z-20 max-w-[1650px] mx-auto px-6 lg:px-20">
          <Reveal>
            <div className="text-center max-w-[850px] mx-auto">
              <SectionBadge label="GLOBAL WELLNESS COMMUNITY" dark />
              <h2 className="text-[46px] md:text-[64px] leading-[1.02] tracking-[-1px] font-semibold text-white mt-6" style={{ fontFamily: "Poppins" }}>
                Our Growing<br />Global Presence
              </h2>
              <p className="text-white/75 text-[17px] md:text-[19px] leading-[38px] mt-8 max-w-[760px] mx-auto">
                Empowering students worldwide through authentic yoga, mindful living, and transformative wellness experiences.
              </p>
            </div>
          </Reveal>
          
          <Reveal delay={0.15}>
            <div className="relative mt-8">
              <div className="relative overflow-hidden rounded-[48px] border border-white/15 bg-white/10 backdrop-blur-2xl shadow-[0_40px_140px_rgba(0,0,0,0.12)]">
                <div className="absolute top-[-120px] left-[-120px] w-[260px] h-[260px] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.08)_0%,transparent_70%)] pointer-events-none" />
                <div className="grid md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-white/10">
                  {stats.map((item, i) => (
                    <motion.div key={i} whileHover={{ y: -8, backgroundColor: "rgba(255,255,255,0.04)" }} transition={{ duration: 0.4 }}
                      style={{ willChange: "transform" }}
                      className="group relative overflow-hidden px-8 py-14 text-center">
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-700 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.08),transparent_70%)] pointer-events-none" />
                      <motion.h3 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: i * 0.1 }}
                        className="text-[56px] md:text-[64px] leading-none tracking-[-3px] font-semibold text-white relative z-20">
                        <motion.span initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: i * 0.15 }}>
                          {item.number}
                        </motion.span>
                      </motion.h3>
                      <div className="w-14 h-[2px] bg-gradient-to-r from-[#F29A0C] to-transparent mx-auto mt-6" />
                      <p className="text-white/70 text-sm leading-[28px] mt-6 relative z-20">{item.label}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="relative overflow-hidden py-16">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF8EE] via-[#FFE5C2] to-[#FFFDF8]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,154,12,0.14),transparent_30%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(242,154,12,0.12),transparent_30%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.30),transparent_45%)]" />
        <motion.div animate={{ scale: [1, 1.08, 1], opacity: [0.12, 0.22, 0.12] }} transition={{ duration: 10, repeat: Infinity }}
          className="absolute top-[-240px] left-[-180px] w-[850px] h-[850px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.12)_0%,transparent_70%)] pointer-events-none will-change-transform" style={{ transform: "translateZ(0)" }} />
        <motion.div animate={{ scale: [1, 1.05, 1], opacity: [0.08, 0.14, 0.08] }} transition={{ repeat: Infinity, duration: 12 }}
          className="absolute bottom-[-240px] right-[-180px] w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.4)_0%,transparent_70%)] pointer-events-none will-change-transform" style={{ transform: "translateZ(0)" }} />
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 160, ease: "linear" }}
          style={{ willChange: "transform" }}
          className="absolute top-[10%] right-[5%] w-[520px] h-[520px] rounded-full border border-[#F29A0C]/10 pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay bg-[url('/assets/image 43.jpeg')]" />

        <div className="relative z-20 max-w-[1650px] mx-auto px-6 lg:px-20">
          <Reveal>
            <div className="text-center max-w-[900px] mx-auto">
              <SectionBadge label="STUDENT TESTIMONIALS" />
              <h2 className="text-[46px] md:text-[64px] leading-[1.2] tracking-[-1px] font-semibold text-[#111111] mt-6" style={{ fontFamily: "Poppins" }}>
                Transforming Lives<br />Through Yoga
              </h2>
              <p className="text-[#666666] text-[17px] md:text-[19px] leading-[38px] mt-8 max-w-[760px] mx-auto">
                Discover how students across the world are transforming their wellness journey through authentic yoga, mindfulness, and holistic wellness practices.
              </p>
            </div>
          </Reveal>
          
          <Reveal delay={0.15}>
            <div className="grid lg:grid-cols-[480px_1fr] gap-14 items-center mt-8">
              <div className="relative hidden lg:block">
                <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 6 }}
                  style={{ willChange: "transform" }}
                  className="relative overflow-hidden rounded-[48px] h-[760px] border border-[#F4D9B5] shadow-[0_40px_140px_rgba(0,0,0,0.08)]">
                  <img src="/assets/image 43.jpeg" alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 w-full p-10">
                    <SectionBadge label="GLOBAL WELLNESS COMMUNITY" dark />
                    <h3 className="text-white text-[42px] leading-[1] tracking-[-3px] font-semibold mt-5">1000+<br />Happy Students</h3>
                    <p className="text-white/70 text-[15px] leading-[30px] mt-6 max-w-[340px]">
                      Empowering students worldwide through authentic yoga experiences, mindfulness, and holistic wellness.
                    </p>
                  </div>
                </motion.div>
                <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 5 }}
                  style={{ willChange: "transform" }}
                  className="absolute -right-10 top-14 rounded-[34px] border border-white/30 bg-white/75 backdrop-blur-2xl p-8 shadow-[0_30px_120px_rgba(0,0,0,0.08)]">
                  <div className="flex items-center gap-5">
                    <div className="w-20 h-20 rounded-[28px] bg-gradient-to-br from-[#F29A0C] to-[#FF7A00] flex items-center justify-center text-white text-3xl shadow-[0_20px_60px_rgba(242,154,12,0.25)]">
                      <FaQuoteLeft />
                    </div>
                    <div>
                      <h4 className="text-[#111111] text-[34px] leading-none tracking-[-2px] font-semibold">98%</h4>
                      <p className="text-[#666666] text-sm mt-3">Satisfaction Rate</p>
                    </div>
                  </div>
                </motion.div>
              </div>
              
              <div className="space-y-4">
                {testimonials.map((item, i) => (
                  <motion.div key={i}
                    initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: i * 0.08 }}
                    whileHover={{ y: -8, scale: 1.01 }}
                    style={{ willChange: "transform" }}
                    className="group relative overflow-hidden rounded-[40px] border border-[#F4D9B5] bg-white/75 backdrop-blur-2xl p-6 shadow-[0_30px_120px_rgba(0,0,0,0.05)]">
                    <div className="absolute top-[-80px] right-[-80px] w-[200px] h-[200px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.08)_0%,transparent_70%)] pointer-events-none" />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-700 bg-[radial-gradient(circle_at_top_right,rgba(242,154,12,0.08),transparent_60%)] pointer-events-none" />
                    <div className="relative z-20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-5">
                          <div className="relative w-20 h-20 rounded-[28px] overflow-hidden border border-[#F4D9B5]">
                            <img src={`https://i.pravatar.cc/200?img=${i + 11}`} alt="" className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110" style={{ willChange: "transform" }} />
                          </div>
                          <div>
                            <h4 className="text-[#111111] text-[22px] font-semibold">{item.name}</h4>
                            <p className="text-[#777777] text-sm mt-2">{item.location}</p>
                          </div>
                        </div>
                        <div className="w-16 h-16 rounded-[24px] bg-[#F29A0C]/10 flex items-center justify-center text-[#F29A0C] text-2xl">
                          <FaQuoteLeft />
                        </div>
                      </div>
                      
                      <p className="text-[#555555] text-[17px] leading-[38px] mt-6">"{item.review}"</p>
                      
                      <div className="flex flex-wrap items-center justify-between gap-4 mt-8 pt-6 border-t border-[#F4D9B5]/30">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-[2px] text-[18px] font-semibold leading-none">
                            <span className="text-[#4285F4]">G</span><span className="text-[#EA4335]">o</span>
                            <span className="text-[#FBBC05]">o</span><span className="text-[#4285F4]">g</span>
                            <span className="text-[#34A853]">l</span><span className="text-[#EA4335]">e</span>
                          </div>
                          <p className="text-[#666666] text-[10px] tracking-[2px] uppercase">Verified Review</p>
                        </div>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, si) => (
                            <motion.span key={si} whileHover={{ scale: 1.2 }} className="text-[#F29A0C] text-lg cursor-default">★</motion.span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative overflow-hidden py-20 bg-[#FFF8EE]">
        <div className="absolute inset-0">
          <img src="/assets/image 9.jpeg" alt="Wellness Background" className="w-full h-full object-cover opacity-[0.99]" />
          <div className="absolute top-[-180px] left-[-120px] w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.1)_0%,transparent_70%)] pointer-events-none" />
          <div className="absolute bottom-[-220px] right-[-150px] w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(255,179,71,0.08)_0%,transparent_70%)] pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#FFF8EE]/65 via-[#FFF3E0]/50 to-[#FFFFFF]/60" />
          <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] pointer-events-none">
            <img src="/assets/logo_12.png" alt="" className="w-[620px] object-contain" />
          </div>
        </div>

        <div className="relative z-20 max-w-[1350px] mx-auto px-6 lg:px-20">
          <Reveal>
            <motion.div
              onMouseMove={handleMouseMove}
              onMouseLeave={() => { rotateX.set(0); rotateY.set(0); }}
              style={{ rotateX: smoothRotateX, rotateY: smoothRotateY, transformPerspective: 1400, transformStyle: "preserve-3d", willChange: "transform" }}
              className="relative overflow-hidden rounded-[42px] border border-[#F4D9B5] bg-gradient-to-br from-white/60 via-[#FFF8EE]/80 to-[#FFE8C7]/90 backdrop-blur-3xl shadow-[0_30px_100px_rgba(242,154,12,0.08)] px-8 py-12 md:px-14 md:py-14">
              <div className="absolute top-[-100px] right-[-60px] w-[240px] h-[240px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.25)_0%,transparent_70%)] pointer-events-none" />
              <div className="absolute bottom-[-100px] left-[-60px] w-[240px] h-[240px] rounded-full bg-[radial-gradient(circle,rgba(255,179,71,0.25)_0%,transparent_70%)] pointer-events-none" />

              <div style={{ transform: "translateZ(40px)" }} className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-14 items-center">
                <div className="text-left">
                  <div className="inline-flex items-center gap-3 rounded-full border border-[#F4D9B5] bg-white/70 px-5 py-3 backdrop-blur-xl">
                    <div className="w-2 h-2 rounded-full bg-[#F29A0C] animate-pulse" />
                    <span className="text-[#F29A0C] text-[10px] tracking-[4px] uppercase font-semibold">BEGIN YOUR WELLNESS JOURNEY</span>
                  </div>
                  <h2 className="text-[42px] md:text-[68px] leading-[1.2] tracking-[-1px] font-semibold text-[#111111] mt-8" style={{ fontFamily: "Poppins" }}>
                    Experience The<br /><span className="text-[#F29A0C]">Power Of Yoga</span>
                  </h2>
                  <p className="text-[#222222] text-[17px] leading-[34px] mt-6 max-w-[700px]">
                    Join a growing wellness community transforming lives through yoga, meditation, mindfulness, and holistic healing experiences.
                  </p>
                  <div className="flex flex-wrap justify-start gap-3 mt-6">
                    {["Mindfulness", "Meditation", "Healing", "Balance"].map((item, i) => (
                      <motion.div key={i} whileHover={{ y: -3, scale: 1.04 }}
                        style={{ willChange: "transform" }}
                        className="rounded-full border border-[#F4D9B5] bg-white/60 backdrop-blur-xl px-5 py-2.5 text-[#444444] text-xs font-medium shadow-[0_8px_30px_rgba(242,154,12,0.04)]">
                        {item}
                      </motion.div>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 mt-10">
                    <Link to="/register">
                      <motion.button whileHover={{ scale: 1.05 }} style={{ willChange: "transform" }} className="rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8A00] text-white px-8 py-4 text-[14px] font-semibold shadow-[0_20px_60px_rgba(242,154,12,0.25)] flex items-center gap-3 cursor-pointer">
                        Start Your Journey<FaArrowRight />
                      </motion.button>
                    </Link>
                  </div>
                </div>
                <div className="hidden md:block relative overflow-hidden rounded-[30px] border border-[#F4D9B5] bg-gradient-to-br from-white/80 to-[#FFF3E0]/80 backdrop-blur-2xl p-2 h-[400px] shadow-[0_20px_60px_rgba(242,154,12,0.08)]">
                  <img src="/assets/image 16.jpeg" alt="Yoga Practice" className="w-full h-full object-cover rounded-[22px] opacity-[0.95]" />
                </div>
              </div>
            </motion.div>
          </Reveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          LIGHTBOX   for milestone gallery images
      ══════════════════════════════════════════ */}
      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            onClick={()=>setLightbox(null)}
            className="fixed inset-0 z-[999] flex items-center justify-center p-6"
            style={{ background:"rgba(10,5,0,0.94)", backdropFilter:"blur(28px)" }}>

            <motion.button onClick={()=>setLightbox(null)} whileHover={{ scale:1.1, background:"rgba(242,154,12,0.2)" }}
              className="absolute top-6 right-6 w-12 h-12 rounded-full flex items-center justify-center cursor-pointer border"
              style={{ background:"rgba(255,255,255,0.08)", borderColor:"rgba(255,255,255,0.15)", color:"#fff", border:"none" }}>
              <FiX size={20}/>
            </motion.button>

            <motion.div
              initial={{ scale:0.85, opacity:0, y:30 }} animate={{ scale:1, opacity:1, y:0 }}
              exit={{ scale:0.88, opacity:0 }} transition={{ duration:0.45, ease:[0.22,1,0.36,1] }}
              onClick={e=>e.stopPropagation()}
              className="relative w-auto max-w-[90vw] rounded-[32px] overflow-hidden"
              style={{ boxShadow:"0 60px 140px rgba(0,0,0,0.85), 0 0 0 1px rgba(242,154,12,0.25)" }}>
              <div className="absolute top-0 left-0 right-0 h-[3px]"
                style={{ background:"linear-gradient(90deg,transparent,#F29A0C,#FFD166,transparent)" }}/>
              <img src={lightbox.src} alt={lightbox.label}
              className="block w-auto h-auto max-w-[90vw] max-h-[85vh] object-contain"/>
              <div className="absolute bottom-0 left-0 right-0 p-8"
                style={{ background:"linear-gradient(to top,rgba(10,5,0,0.94),transparent)" }}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[1.5px]"
                    style={{ background:"linear-gradient(135deg,#F29A0C,#E97818)", color:"#fff" }}>{lightbox.tag}</span>
                </div>
                <p className="font-black text-white text-[22px]" style={{ fontFamily:"'Poppins',sans-serif" }}>{lightbox.label}</p>
                <p className="text-[13px] mt-1.5" style={{ color:"rgba(255,255,255,0.45)" }}>
                  Divya Nithin Bhojwani · World Yogasana Championship · Ahmedabad 2024
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default About;